import React from 'react';
import { definePage, defineResource } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import EmployeeDayOffForm from 'src/components/Employee/EmployeeDayOffForm';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import {
  tenantEmployeeDayOff,
  tenantEmployeeDayOffCreate,
  tenantEmployeeDayOffDelete,
  tenantEmployeeDayOffUpdate,
  tenantEmployees,
} from 'src/services/apis/tenantRequests';
import { EmployeeDayOffEntity } from 'src/services/apis/types';
import { employeeName } from 'src/utils/employee';
import { QUERIES } from 'src/utils/react-query';
import { splitTags } from 'src/utils/tags';

const employeeDayOff = defineResource({
  key: QUERIES.EMPLOYEE_DAY_OFF,
  // The hand-written hook sent no limit, so the server (alwaysPaginate, no configured take)
  // returned every row in one shot; the crud layer's paginated query needs a concrete page
  // size, and 1000 is the practical match — the same reach the pilot used for an
  // effectively-unbounded list.
  pageSize: 1000,
  api: {
    list: tenantEmployeeDayOff,
    create: tenantEmployeeDayOffCreate,
    update: tenantEmployeeDayOffUpdate,
    remove: tenantEmployeeDayOffDelete,
  },
  // The hand-written page filtered title/reason in memory over the fetched set.
  search: 'client',
  searchFields: ['title', 'reason'],
  // Both server columns. The employee filter is the one place this page loads the
  // employees list — the column itself reads the name off the eagerly joined row.
  filterable: [
    {
      field: 'employeeId',
      labelKey: 'common.employee',
      type: 'reference',
      load: tenantEmployees,
      optionLabel: (employee: any) =>
        [employee.user?.firstName, employee.user?.lastName].filter(Boolean).join(' ') || employee.id,
    },
    { field: 'date', labelKey: 'common.date', type: 'date' },
  ],
});

export const employeeDayOffPage = definePage(employeeDayOff, {
  // The hand-written page reused the employee RULES page title, and a migration does not
  // relabel the page it replaces.
  titleKey: 'titles.employeeRulesPage',
  descriptionKey: 'common.employeeRules',
  createLabelKey: 'actions.createNewEmployeeDayOff',
  updateLabelKey: 'actions.updateEmployeeDayOff',
  deleteHeadingKey: 'messages.areYouSureToDeleteThisEmployeeDayOff',
  columns: [
    // The column a user edits by title must survive every prefs reconciliation.
    { key: 'title', headerKey: 'common.title', sortable: true, alwaysVisible: true },
    { key: 'reason', headerKey: 'common.reason' },
    // The hand-written cell rendered the raw VND integer (and NaNK whenever amount was
    // absent); the currency kind renders it the way the rest of the app reads money.
    { key: 'amount', headerKey: 'common.amount', display: { kind: 'currency' } },
    {
      key: 'employee',
      headerKey: 'common.employee',
      // The server eagerly joins employee + user, so the name comes from the row itself —
      // the old page never fetched an employees list, so no reference load is added here.
      // The literal 'None' fallback is preserved verbatim, wart included.
      render: (row: EmployeeDayOffEntity) => employeeName(row.employee) || 'None',
    },
  ],
  form: EmployeeDayOffForm,
  emptyState: <EmptyState />,
  toPayload: (values: any, params) => ({
    ...values,
    restaurantId: params.restaurantId,
    tags: splitTags(values.tags),
  }),
  useTitleVars: () => ({ restaurantName: useCurrentRestaurant()?.name }),
});
